import { Link, useParams } from 'react-router-dom';
import { useT } from '../../i18n';
import { L } from '../tokens';
import { useViewportKind } from '../useViewport';
import { useCasesFilter } from '../casesFilterContext';
import { Card, Message, Notes, PeriodPills, ReportPage } from './ReportParts';
import { periodBody } from './reportsApi';
import { countText, customerName, supplierName } from './reportFormat';
import { useReportPeriod } from './periodStore';
import { useReport } from './useReport';
import { DocumentsCard, MoneyPair, ProblemsCard, RankingTable } from './WhoParts';

const KINDS = ['this_week', 'this_month', 'this_year', 'custom'];

// The Cases card is its own request: it reuses the Cases classification, which takes longer than the money figures, so the page does not wait for it.
function CasesCard({ id }) {
  const t = useT();
  const { setSupplierIds } = useCasesFilter();
  const { status, data, retry } = useReport('supplier-cases', { id });
  return (
    <Card label={t('reports.supplier.cases')} testid="supplier-cases">
      {status === 'loading' && <span aria-busy="true" style={{ color: L.muted }}>{t('reports.loading')}</span>}
      {status === 'error' && <button type="button" onClick={retry} style={{ font: 'inherit', cursor: 'pointer' }}>{t('reports.error')} {t('reports.retry')}</button>}
      {status === 'ready' && ['attention', 'open', 'matched'].map((k) => (
        <Link key={k} to={`/cases/${k}`} onClick={() => setSupplierIds([id])} data-testid={`supplier-cases-${k}`}
          style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: `1px solid ${L.rowLine}`, color: L.text, textDecoration: 'none', fontSize: 16 }}>
          <span>{t(`reports.cases.${k}`)}</span><span style={{ fontWeight: 600 }}>{countText(data.cases[k])}</span>
        </Link>
      ))}
    </Card>
  );
}

// Leo's "Supplier page" (updated board): Orders and Invoiced, Documents per type, the discrepancy types found with Documents late and Price increases apart, the customers they sell to
// ranked by order value, and the Cases card. Commission and Commission forecast are gone from this page.
export default function SupplierView() {
  const t = useT();
  const { id } = useParams();
  const phone = useViewportKind() === 'phone';
  const [period, setPeriod] = useReportPeriod('past');
  const { status, data, retry } = useReport('supplier', { id, ...periodBody(period) });
  const title = data && data.supplier ? supplierName(data.supplier, t) : t('reports.suppliers.title');
  const two = { display: 'grid', gridTemplateColumns: phone ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 18 };
  return (
    <ReportPage title={title} backTo="/reports/suppliers" backLabel={t('reports.suppliers.title')} pills={<PeriodPills kinds={KINDS} value={period} onChange={setPeriod} />} testid="report-supplier">
      {status !== 'ready' ? <Message status={status} onRetry={retry} /> : (
        <>
          <MoneyPair data={data} prefix="sp" phone={phone} />
          <div style={two}>
            <DocumentsCard documents={data.documents} prefix="sp" />
            <ProblemsCard problems={data.problems} currency={data.currency} prefix="sp" />
          </div>
          <RankingTable title={t('reports.supplier.buyers')} testid="supplier-buyers" currency={data.currency}
            columns={[{ key: 'orders', label: t('reports.who.col.orders') }, { key: 'order_value', label: t('reports.who.col.orderValue'), money: true }, { key: 'invoiced', label: t('reports.who.col.invoiced'), money: true, strong: true }]}
            rows={data.buyers.map((b, i) => ({ id: b.customer ? b.customer.id : `none-${i}`, testid: b.customer ? `sp-buyer-${b.customer.id}` : 'sp-buyer-none', name: customerName(b.customer, t), to: b.customer ? `/reports/customers/${b.customer.id}` : null, bar: b.bar, orders: b.orders, order_value: b.order_value, invoiced: b.invoiced }))} />
          <CasesCard id={id} />
          <Notes notes={data.notes} />
        </>
      )}
    </ReportPage>
  );
}
